/**
 * GenericSummaryMap — template SƠ ĐỒ KHO chung cho mọi factory chưa có
 * layout riêng. Tự detect zones từ `mapCells` bằng 2 segment đầu của
 * `fullAreaCode` (vd "NT.A.X1" → zone "NT.A"). Mỗi zone render thành 1 card
 * với grid 4 cột auto-flow chứa các ô vùng + tồn (Kg). Bên dưới là section
 * "Phân bổ tồn kho" group theo zone + KV SX (WIP).
 *
 * Khi factory có layout không gian thực: tạo `<Factory>SummaryMap.tsx` và
 * đăng ký vào `FactorySummaryMap.tsx` → template này tự nhường chỗ.
 */
import { useMemo } from 'react'
import { alpha, type Theme } from '@mui/material/styles'
import { Box, Paper, Stack, Typography } from '@mui/material'
import type { FactoryCell, FactorySummaryWarehouseSplit } from '@/store/api/factoryApi'
import { CARD_GRID_PROGRESSIVE, PANE_8_2 } from '@/theme/responsive'
import { BlockTitle } from './BlockTitle'
import type { FactorySummaryMapCell } from '../baoLoc/FactoryBaoLocSummaryMap'

interface Props {
  mapCells: FactorySummaryMapCell[]
  sumRow?: Record<string, FactoryCell> | null
  kvSx?: number | null
  warehouseSplit?: FactorySummaryWarehouseSplit | null
  scope?: 'all' | 'kho-a' | 'kho-bc' | 'allocation'
  title?: string
  subtitle?: string
  factoryName?: string
}

function asNumber(v: FactoryCell): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function fmtQty(v: FactoryCell | number): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

/** Lấy zone prefix = 2 segment đầu; nếu chỉ 1 segment hoặc rỗng → "Khác". */
function zonePrefix(field: string): string {
  const parts = field.split('.')
  if (parts.length >= 2) return `${parts[0]}.${parts[1]}`
  return field || 'Other'
}

/** Bỏ zone prefix khỏi field → short label cho ô. "NT.A.X1" → "X1". */
function cellShort(field: string): string {
  const parts = field.split('.')
  return parts.length >= 3 ? parts.slice(2).join('.') : field
}

// Style dòng allocation — lấy Bảo Lộc làm chuẩn (allocationRowSx).
const allocationRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 1,
  py: 0.4,
  px: 0.75,
  borderRadius: 0.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(0,0,0,0.02)',
}

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 0.5,
  px: 0.75,
  py: 0.5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
  '&:hover': { bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.09) },
} as const

export default function GenericSummaryMap({
  mapCells,
  kvSx,
  warehouseSplit,
  title,
  subtitle,
  factoryName,
}: Props) {
  const empty = !mapCells.length

  // Group cells theo zone prefix; sort zones alpha + cells trong zone theo
  // short label tự nhiên (e.g. A1, A2, A3 …).
  const zones = useMemo(() => {
    const byZone = new Map<string, FactorySummaryMapCell[]>()
    for (const c of mapCells) {
      const key = zonePrefix(c.field || '')
      const arr = byZone.get(key) ?? []
      arr.push(c)
      byZone.set(key, arr)
    }
    return Array.from(byZone.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([zone, cells]) => ({
        zone,
        cells: cells.sort((a, b) => cellShort(a.field).localeCompare(cellShort(b.field), 'en', { numeric: true })),
        total: cells.reduce((s, c) => s + (asNumber(c.value) ?? 0), 0),
      }))
  }, [mapCells])

  // Allocation: số do API trả về (`summaryWarehouseSplit`), KHÔNG tự cộng lại
  // client-side. Fallback gom từ mapCells khi API thiếu split.
  const allocationRows = useMemo(() => {
    if (warehouseSplit) {
      const rows: Array<{ labelVi: string; value: number; bold?: boolean }> = [
        { labelVi: 'Total (stack + WIP)', value: warehouseSplit.totalStackAndKv, bold: true },
      ]
      if (warehouseSplit.zoneAWithKv) rows.push({ labelVi: 'Warehouse A + WIP', value: warehouseSplit.zoneAWithKv })
      if (warehouseSplit.zoneB) rows.push({ labelVi: 'Warehouse B', value: warehouseSplit.zoneB })
      if (warehouseSplit.zoneC) rows.push({ labelVi: 'Warehouse C', value: warehouseSplit.zoneC })
      if (warehouseSplit.noArea) rows.push({ labelVi: 'Unassigned area', value: warehouseSplit.noArea })
      rows.push({ labelVi: 'WIP Area', value: warehouseSplit.kvSx })
      return rows
    }
    let noArea = 0
    for (const c of mapCells) {
      if (!c.field || !c.field.includes('.')) noArea += asNumber(c.value) ?? 0
    }
    const kv = asNumber(kvSx ?? null) ?? 0
    const zoneTotal = zones.reduce((s, z) => s + z.total, 0)
    const rows: Array<{ labelVi: string; value: number; bold?: boolean }> = [
      { labelVi: 'Total (stack + WIP)', value: zoneTotal + noArea + kv, bold: true },
    ]
    for (const z of zones) rows.push({ labelVi: `Warehouse ${z.zone}.*`, value: z.total })
    if (noArea > 0) rows.push({ labelVi: 'Unassigned area', value: noArea })
    if (kv > 0) rows.push({ labelVi: 'WIP area', value: kv })
    return rows
  }, [mapCells, kvSx, zones, warehouseSplit])

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BlockTitle title={title ?? 'Factory map'} />

      <Box sx={{ ...PANE_8_2, gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' }, gap: 1.5, alignItems: 'start' }}>
      {empty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            No warehouse map data
          </Typography>
        </Box>
      ) : (
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={1.25}>
            {zones.map((z) => (
              <Box key={z.zone}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      letterSpacing: 0.04,
                      color: 'primary.dark',
                    }}
                  >
                    Warehouse {z.zone}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Σ {fmtQty(z.total)}
                  </Typography>
                </Box>
                <Box sx={{ ...CARD_GRID_PROGRESSIVE, gap: 0.5 }}>
                  {z.cells.map((c) => (
                    <Box key={c.field} sx={cellSx} title={c.field}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.15 }}>
                        {cellShort(c.field) || c.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {fmtQty(c.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          minWidth: 0,
          pl: { xs: 0, md: 1 },
          borderLeft: { xs: 0, md: '1px dashed' },
          borderColor: 'divider',
        }}
      >
        <BlockTitle title="Stock allocation" />
        <Stack spacing={0.5}>
          {allocationRows.map((r) => (
            <Box key={r.labelVi} sx={allocationRowSx}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
                {r.labelVi}
              </Typography>
              <Typography variant="caption" fontWeight={800}>
                {fmtQty(r.value)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      </Box>
    </Paper>
  )
}
